import type { ChartPanelConfig, MeasurementField } from "@/types/measurement";

function clearUnavailableSource(
  panel: ChartPanelConfig,
  fieldMap: Map<string, MeasurementField>,
): ChartPanelConfig {
  const { source, ...panelWithoutSource } = panel;
  const fieldIds =
    source?.type === "FIELD_CHART" && fieldMap.has(source.fieldId)
      ? [source.fieldId]
      : [];

  return {
    ...panelWithoutSource,
    fieldIds,
    fieldOverrides: {},
  };
}

function fieldChartForSource(
  panel: ChartPanelConfig,
  fieldMap: Map<string, MeasurementField>,
): ChartPanelConfig | null {
  if (panel.source?.type !== "FIELD_CHART") return null;

  const field = fieldMap.get(panel.source.fieldId);
  const fieldPanels = field?.displayConfig?.chartPanels ?? [];
  const requestedPanel = panel.source.panelId
    ? fieldPanels.find((candidate) => candidate.id === panel.source?.panelId)
    : fieldPanels[0];
  const sourcePanel = requestedPanel ?? fieldPanels[0];

  if (!sourcePanel) return clearUnavailableSource(panel, fieldMap);

  const resolvedSource = requestedPanel
    ? panel.source
    : { ...panel.source, panelId: sourcePanel.id };
  const sourceFieldIds = sourcePanel.fieldIds.length
    ? sourcePanel.fieldIds
    : [panel.source.fieldId];
  const allSourceFieldsAvailable = sourceFieldIds.every((fieldId) =>
    fieldMap.has(fieldId),
  );

  if (!allSourceFieldsAvailable) {
    return {
      ...panel,
      chartType: sourcePanel.chartType,
      label: panel.label ?? sourcePanel.label,
      fieldIds: [panel.source.fieldId],
      fieldOverrides: {},
      source: resolvedSource,
    };
  }

  return {
    ...sourcePanel,
    id: panel.id,
    label: panel.label ?? sourcePanel.label,
    fieldIds: sourceFieldIds,
    fieldOverrides: {
      ...(sourcePanel.fieldOverrides ?? {}),
      ...(panel.fieldOverrides ?? {}),
    },
    source: resolvedSource,
  };
}

export function resolveChartPanelConfig(
  panel: ChartPanelConfig,
  fieldMap: Map<string, MeasurementField>,
): ChartPanelConfig {
  return fieldChartForSource(panel, fieldMap) ?? panel;
}

export function resolveChartPanelConfigs(
  panels: ChartPanelConfig[] | null | undefined,
  fieldMap: Map<string, MeasurementField>,
): ChartPanelConfig[] | undefined {
  if (!panels?.length) return undefined;
  return panels.map((panel) => resolveChartPanelConfig(panel, fieldMap));
}
